import { defineStore } from 'pinia'
import streamerApi from '../api/streamerApi'
import { PointerType } from '../enums/pointerType'
import {
    CURSOR_CHANGE_HIDE,
    CURSOR_CHANGE_MODE,
    CURSOR_CHANGE_POSITION,
    CURSOR_CHANGE_SHAPE,
    USER_INTERACTION,
    mEvent,
} from '../utils/events'
import rtcp from '../utils/rtcp'

/**
 * Dynamic imports are used throughout this store to prevent circular dependencies.
 * The cursor store needs to interact with main, user, machine, groupSession, supervisionRoom,
 * menu, and modal stores, many of which may also reference the cursor store.
 * Using dynamic imports breaks these circular references by delaying resolution until runtime.
 */

interface ServerPositionReceived {
    mouse_x: number
    mouse_y: number
}

interface BrowserPositionChangedEvent {
    x: number
    y: number
}

interface TouchData {
    id: number
    x: number
    y: number
}

interface ClickData {
    id: string
    data: string
}

interface State {
    // Capture mode
    isRemoteCursorCapturedModeInitialized: boolean
    _pendingUserCaptureModeChange: boolean | null
    _cursorInitEpoch: number
    isLocalCursorVisible: boolean

    // Touch and pointer
    isTouchModeEnabled: boolean
    pointerType: PointerType | null
    simultaneousTouches: TouchData[]

    // Click events
    pendingLocalClickDownEvent: ClickData | null
    mouseWheelThrottleCounter: number

    // Cursor object
    cursor: {
        width: number
        height: number
        imageSrc: string
        remoteHotSpot: { x: number; y: number }
        localHotSpot: { x: number; y: number }
        remotePosition: { x: number; y: number }
        localPosition: { x: number; y: number }
    }

    // Video object
    video: {
        contentWidth: number
        contentHeight: number
        aspectRatio: number
        centerOffset: {
            x: number
            y: number
        }
        serverToBrowserScale: {
            width: number
            height: number
        }
    }
}

function _calculateRemotePositionFromEvent(
    event: MouseEvent | PointerEvent,
    centerOffset: { x: number; y: number },
    serverToBrowserScale: { width: number; height: number }
): { x: number; y: number } {
    return {
        x: Math.round(
            (event.clientX - centerOffset.x) * serverToBrowserScale.width
        ),
        y: Math.round(
            (event.clientY - centerOffset.y) * serverToBrowserScale.height
        ),
    }
}

function _getVideoElement(): HTMLVideoElement {
    const videoElement = document.getElementById(
        'video'
    ) as HTMLVideoElement | null

    if (!videoElement) {
        throw new Error('#video element has not been found.')
    }

    return videoElement
}

export const useCursorStore = defineStore('cursor', {
    state: (): State => ({
        // Capture mode
        isRemoteCursorCapturedModeInitialized: false,
        _pendingUserCaptureModeChange: null,
        _cursorInitEpoch: 0,
        isLocalCursorVisible: true,

        // Touch and pointer
        isTouchModeEnabled: false,
        pointerType: null,
        simultaneousTouches: [],

        // Click events
        pendingLocalClickDownEvent: null,
        mouseWheelThrottleCounter: 0,

        // Cursor object
        cursor: {
            width: 0,
            height: 0,
            imageSrc:
                'data:image/png,%89PNG%0D%0A%1A%0A%00%00%00%0D%49%48%44%52%00%00%00%29%00%00%00%27%08%06%00%00%00%92%6A%61%86%00%00%02%4F%49%44%41%54%58%85%ED%97%4D%6B%D4%50%14%86%9F%93%49%67%C6%CE%1D%2B%0A%05%41%2A%BA%91%01%61%8A%54%44%FC%40%29%E2%42%FF%82%6B%BB%F0%6F%B8%77%29%FE%04%37%22%BA%71%21%08%5D%28%2E%2A%A8%B4%0A%96%5A%0A%4E%37%62%C5%CC%4C%3A%8D%39%2E%6E%82%69%E7%C3%A9%98%64%61%5E%C8%22%F7%86%F0%DC%F7%9C%BC%F7%06%0A%FD%1B%C9%88%F1%78%4E%A3%2B%37%39%C9%1B%03%2D%57%38%0F%4C%00%55%A0%0C%B8%0C%5F%4C%26%72%F6%0E%54%E0%AE%2B%5C%00%0C%50%07%0E%90%33%A8%BB%77%C0%0B%F5%AA%71%C4%45%B8%17%28%EF%00%1F%0B%D8%01%02%72%28%7D%1F%24%80%17%EA%25%E3%48%19%E1%7E%A0%2C%25%A6%72%01%1D%08%09%E0%85%7A%CE%38%32%81%F0%20%50%5E%25%A6%32%07%1D%0A%09%E0%85%7A%C6%38%B2%80%20%81%F2%32%31%95%29%E8%48%48%00%2F%D4%D9%08%94%BC%40%FF%08%09%E0%85%DA%CC%13%74%2C%48%80%2D%DF%6F%1E%AA%56%73%01%ED%CB%C9%61%72%CB%15%36%BF%6D%35%2B%B0%10%05%FE%61%E0%20%30%49%CA%39%3A%36%24%80%99%9A%62%AD%B5%39%08%34%BB%C0%37%D0%D2%31%B4%B1%BE%AE%35%E1%0D%70%11%38%0D%1C%C5%3A%BA%AF%45%8F%AB%91%3D%E9%B7%DB%54%6B%35%BB%00%47%DE%26%A6%92%87%8E%12%76%8F%2F%91%92%93%43%21%FD%76%9B%69%63%FC%47%4F%9F%54%E7%6F%DC%04%D0%1D%78%DC%B3%C1%BE%8D%DD%2E%BB%D1%B5%43%56%E1%1E%97%BB%EB%79%5A%87%8E%2B%DC%31%F0%39%2E%B1%81%35%E0%1A%70%19%98%05%4E%00%D3%D8%83%48%99%94%CA%C3%00%36%22%D0%EF%C0%57%A0%8D%2D%77%66%90%31%68%DC%AF%1A%01%09%F6%97%A2%8E%CD%44%B0%5B%E2%0F%6C%4F%A6%D6%8F%FB%2D%93%CB%6F%97%15%EB%5E%0F%F8%49%8A%11%F4%37%BD%14%BB%1C%07%7A%FA%5F%74%A1%42%85%0A%15%2A%54%A8%D0%7F%A9%5F%EB%DD%ED%7E%47%80%E7%D9%00%00%00%00%49%45%4E%44%AE%42%60%82',
            remoteHotSpot: { x: 0, y: 0 },
            localHotSpot: { x: 0, y: 0 },
            remotePosition: { x: 0, y: 0 },
            localPosition: { x: 0, y: 0 },
        },

        // Video object
        video: {
            contentWidth: 1,
            contentHeight: 1,
            aspectRatio: 1,
            centerOffset: {
                x: 0,
                y: 0,
            },
            serverToBrowserScale: {
                width: 1,
                height: 1,
            },
        },
    }),

    actions: {
        async initRemoteCursorCaptureMode(): Promise<void> {
            // Claim an epoch synchronously so overlapping runs are ordered: the
            // initial forced init (VDAgent still connecting) vs the run replayed
            // on VD_AGENT_CONNECTED. Only the latest run issues the final command.
            const epoch = ++this._cursorInitEpoch

            const { useUserStore } = await import('./user')
            const { useVdAgentStore } = await import('./vdAgent')
            const userStore = useUserStore()
            const vdAgentStore = useVdAgentStore()

            if (epoch !== this._cursorInitEpoch) {
                return
            }

            // Reset so the enable/disable guards do not short-circuit: init must
            // authoritatively (re-)establish the mode regardless of the last
            // known runtime state.
            this.isRemoteCursorCapturedModeInitialized = false

            // Force capture while the VDAgent is unavailable (absolute pointing
            // needs the guest agent); otherwise apply the persisted user
            // preference, not the runtime mode which the force may have moved.
            const shouldCapture =
                !vdAgentStore.isEnabled || userStore.persistedCursorCaptureMode

            shouldCapture
                ? await this.enableRemoteCursorCapturedMode(epoch)
                : await this.disableRemoteCursorCapturedMode(epoch)

            if (epoch !== this._cursorInitEpoch) {
                return
            }

            this.isRemoteCursorCapturedModeInitialized = true
        },

        subscribeToCursorEvents(): void {
            mEvent.subscribe(CURSOR_CHANGE_SHAPE, (data) => {
                this.handleRemoteCursorShapeChange(data.cursorshape)
            })

            mEvent.subscribe(CURSOR_CHANGE_HIDE, () => {
                this.hideLocalCursor()
            })

            mEvent.subscribe(CURSOR_CHANGE_POSITION, (data) => {
                this.convertRemoteCursorPositionToLocal(data.cursorposition)
            })

            mEvent.subscribe(CURSOR_CHANGE_MODE, async (data) => {
                const { useStore } = await import('./main')
                const mainStore = useStore()

                if (mainStore.readonly) {
                    return
                }

                await this.handleRemoteCapturedCursorModeChange(
                    data.cursormode === 'SERVER'
                )

                mainStore.updateLockState()
            })
        },

        async toggleRemoteCursorCapturedMode(): Promise<void> {
            const { useUserStore } = await import('./user')
            const userStore = useUserStore()

            const shouldCapture =
                !userStore.user.player_preferences
                    .is_cursor_captured_mode_enabled

            // Remember the user intent so its confirmation echo persists the
            // preference, whereas technical forces do not.
            this._pendingUserCaptureModeChange = shouldCapture

            shouldCapture
                ? await this.enableRemoteCursorCapturedMode()
                : await this.disableRemoteCursorCapturedMode()
        },

        async enableRemoteCursorCapturedMode(
            expectedEpoch?: number
        ): Promise<void> {
            const { useStore } = await import('./main')
            const { useMachineStore } = await import('./machine')
            const { useUserStore } = await import('./user')
            const mainStore = useStore()
            const machineStore = useMachineStore()
            const userStore = useUserStore()

            // Prevent cursor capture mode to be enabled
            // for mobile devices and Android OS
            if (mainStore.mobile || machineStore.isRunningOnAndroid) {
                return
            }

            // Prevent enabling if already enabled (except during initialization)
            if (
                this.isRemoteCursorCapturedModeInitialized
                && userStore.user.player_preferences
                    .is_cursor_captured_mode_enabled
            ) {
                return
            }

            // Drop a send superseded by a newer init run: no await may sit
            // between this epoch check and the command below.
            if (
                expectedEpoch !== undefined
                && expectedEpoch !== this._cursorInitEpoch
            ) {
                return
            }

            // Enable remote cursor captured mode
            // User preference will be updated once the change is confirmed by the remote
            streamerApi.enableCursorCapturedMode()
        },

        async disableRemoteCursorCapturedMode(
            expectedEpoch?: number
        ): Promise<void> {
            const { useUserStore } = await import('./user')
            const { useVdAgentStore } = await import('./vdAgent')
            const userStore = useUserStore()
            const vdAgentStore = useVdAgentStore()

            // Prevent cursor capture mode to be disabled
            // when VDAgent is not enabled
            if (!vdAgentStore.isEnabled) {
                return
            }

            // Prevent disabling if already disabled (except during initialization)
            if (
                this.isRemoteCursorCapturedModeInitialized
                && !userStore.user.player_preferences
                    .is_cursor_captured_mode_enabled
            ) {
                return
            }

            // Drop a send superseded by a newer init run: no await may sit
            // between this epoch check and the command below.
            if (
                expectedEpoch !== undefined
                && expectedEpoch !== this._cursorInitEpoch
            ) {
                return
            }

            // Disable remote cursor captured mode
            // User preference will be updated once the change is confirmed by the remote
            streamerApi.disableCursorCapturedMode()
        },

        async handleRemoteCapturedCursorModeChange(
            isCursorInCapturedMode: boolean
        ): Promise<void> {
            const { useUserStore } = await import('./user')
            const userStore = useUserStore()

            // The runtime mode always follows the remote so local input handling
            // (pointer lock, motion vs position) stays consistent with the
            // streamer, even while the mode is forced.
            userStore.setCursorCaptureModeState(isCursorInCapturedMode)

            // A pending user toggle is consumed by the first echo, whatever its
            // value: a matching echo persists the preference, any other one just
            // clears it so a later technical force is never taken for an intent.
            const confirmsUserIntent =
                this._pendingUserCaptureModeChange === isCursorInCapturedMode
            this._pendingUserCaptureModeChange = null

            // Persist without blocking the lock recalculation on the round-trip.
            if (confirmsUserIntent) {
                void userStore.saveCursorCaptureModePreference(
                    isCursorInCapturedMode
                )
            }

            this.requestLatestCursorFromRemote()
        },

        requestLatestCursorFromRemote(): void {
            if (!rtcp.isInputReady()) {
                return
            }

            rtcp.inputSend({
                id: 'SEND_CURSOR_SHAPE',
                data: JSON.stringify({}),
            })
        },

        async updateCursorFromRemote(cursorData: {
            MouseWidth: number
            MouseHeight: number
            Hotspot_x: number
            Hotspot_y: number
            MouseShape: string
        }): Promise<void> {
            this.cursor.width = cursorData.MouseWidth
            this.cursor.height = cursorData.MouseHeight
            this.cursor.imageSrc = cursorData.MouseShape
            this.cursor.remoteHotSpot.x = cursorData.Hotspot_x
            this.cursor.remoteHotSpot.y = cursorData.Hotspot_y

            await this.scaleCursorForLocalDisplay()
        },

        async scaleCursorForLocalDisplay(): Promise<void> {
            if (!this.cursor.width || !this.cursor.height) {
                return
            }

            const { useStore } = await import('./main')
            const mainStore = useStore()

            if (mainStore.mobile) {
                return
            }

            const videoElement = _getVideoElement()

            const widthRatio =
                videoElement.offsetWidth / videoElement.videoWidth
            const heightRatio =
                videoElement.offsetHeight / videoElement.videoHeight

            // Scale cursor dimensions to match video display size
            this.cursor.width = this.cursor.width * widthRatio
            this.cursor.height = this.cursor.height * heightRatio

            // Scale hotspot coordinates to match cursor display size
            this.cursor.localHotSpot.x =
                this.cursor.remoteHotSpot.x * widthRatio
            this.cursor.localHotSpot.y =
                this.cursor.remoteHotSpot.y * heightRatio
        },

        async handleRemoteCursorShapeChange(receivedCursor: {
            MouseWidth: number
            MouseHeight: number
            Hotspot_x: number
            Hotspot_y: number
            MouseShape: string
        }): Promise<void> {
            const { useMachineStore } = await import('./machine')
            const machineStore = useMachineStore()

            if (!this.isLocalCursorVisible) {
                this.isLocalCursorVisible = true
            }

            if (machineStore.isRunningOnAndroid) {
                return
            }

            if (
                receivedCursor.MouseWidth === 0
                || receivedCursor.MouseHeight === 0
            ) {
                return
            }

            this.updateCursorFromRemote(receivedCursor)
        },

        hideLocalCursor(): void {
            this.isLocalCursorVisible = false
        },

        completePendingLocalClickDownEvent(): void {
            if (!this.pendingLocalClickDownEvent) {
                return
            }

            // Transform click start events to end events
            this.pendingLocalClickDownEvent.id =
                this.pendingLocalClickDownEvent.id
                    .replace('MOUSEDOWN', 'MOUSEUP')
                    .replace('TOUCHSTART', 'TOUCHEND')

            rtcp.inputSend(this.pendingLocalClickDownEvent)

            this.pendingLocalClickDownEvent = null
        },

        async recalculateVideoMetrics(): Promise<void> {
            const videoElement = _getVideoElement()

            const { useUserStore } = await import('./user')
            const userStore = useUserStore()

            // Calculate actual video content dimensions considering object-fit: contain property
            this.video.aspectRatio =
                videoElement.videoWidth / videoElement.videoHeight

            this.video.contentWidth =
                videoElement.offsetHeight * this.video.aspectRatio
            this.video.contentHeight = videoElement.offsetHeight

            if (this.video.contentWidth > videoElement.offsetWidth) {
                this.video.contentWidth = videoElement.offsetWidth
                this.video.contentHeight =
                    videoElement.offsetWidth / this.video.aspectRatio
            }

            // Calculate video positioning offsets to account for centering with object-fit: contain
            let videoHeight = this.video.contentHeight
            let videoWidth = this.video.contentWidth

            if (!userStore.user.player_preferences.is_upscale_mode_enabled) {
                videoHeight = window.innerHeight
                videoWidth = window.innerWidth
            }

            this.video.centerOffset.x = 0
            this.video.centerOffset.y = 0

            if (videoWidth != videoElement.offsetWidth) {
                this.video.centerOffset.x =
                    Math.abs(videoElement.offsetWidth - videoWidth) / 2
            }
            if (videoHeight != videoElement.offsetHeight) {
                this.video.centerOffset.y =
                    Math.abs(videoElement.offsetHeight - videoHeight) / 2
            }

            // Calculate server to browser scale ratios
            this.video.serverToBrowserScale.width =
                videoElement.videoWidth / this.video.contentWidth
            this.video.serverToBrowserScale.height =
                videoElement.videoHeight / this.video.contentHeight
        },

        async convertRemoteCursorPositionToLocal(
            positionReceived:
                ServerPositionReceived | BrowserPositionChangedEvent
        ): Promise<void> {
            const { useGroupSessionStore } = await import('./groupSession')
            const { useSupervisionRoomStore } =
                await import('./supervisionRoom')
            const { useStore } = await import('./main')
            const { useMachineStore } = await import('./machine')

            const groupSessionStore = useGroupSessionStore()
            const supervisionRoomStore = useSupervisionRoomStore()
            const mainStore = useStore()
            const machineStore = useMachineStore()

            let border =
                supervisionRoomStore.isSupervisionRoomInitialized
                || groupSessionStore.isGroupSessionInitialized
                    ? 4
                    : 0

            // Cursor Captured Mode Enabled
            // OR Player Readonly Mode Enabled
            const { useUserStore } = await import('./user')
            const userStore = useUserStore()

            if (
                userStore.user.player_preferences
                    .is_cursor_captured_mode_enabled
                || mainStore.readonly
            ) {
                // Receiving position from the server, we store it for mousedown and mouseup
                if (
                    'mouse_x' in positionReceived
                    && 'mouse_y' in positionReceived
                ) {
                    this.cursor.remotePosition.x = positionReceived.mouse_x
                    this.cursor.remotePosition.y = positionReceived.mouse_y
                }

                await this.recalculateVideoMetrics()

                const hotX = machineStore.isRunningOnWindows
                    ? 0
                    : this.cursor.remoteHotSpot.x
                      / this.video.serverToBrowserScale.width
                const hotY = machineStore.isRunningOnWindows
                    ? 0
                    : this.cursor.remoteHotSpot.y
                      / this.video.serverToBrowserScale.height

                // Calculate local cursor position with hotspot and border adjustments
                this.cursor.localPosition.x =
                    this.cursor.remotePosition.x
                        / this.video.serverToBrowserScale.width
                    + this.video.centerOffset.x
                    - hotX
                    - border // Add the offset created by object fit
                this.cursor.localPosition.y =
                    this.cursor.remotePosition.y
                        / this.video.serverToBrowserScale.height
                    + this.video.centerOffset.y
                    - hotY
                    - border
            } else {
                // Cursor Captured Mode Disabled
                const hotX = this.cursor.localHotSpot.x
                const hotY = this.cursor.localHotSpot.y

                if ('x' in positionReceived && 'y' in positionReceived) {
                    this.cursor.localPosition.x =
                        positionReceived.x - hotX - border
                    this.cursor.localPosition.y =
                        positionReceived.y - hotY - border
                }
            }
        },

        createTouchDataFromEvent(event: PointerEvent): TouchData {
            return {
                id: event.pointerId,
                x: Math.max(this.cursor.remotePosition.x, 0),
                y: Math.max(this.cursor.remotePosition.y, 0),
            }
        },

        findTouchIndexById(idToFind: number): number {
            for (let i = 0; i < this.simultaneousTouches.length; i++) {
                const touch = this.simultaneousTouches[i]
                if (touch && typeof touch === 'object' && 'id' in touch) {
                    const touchData = touch as TouchData
                    if (touchData.id == idToFind) {
                        return i
                    }
                }
            }
            return -1 // not found
        },

        clearAllTouches(): void {
            this.simultaneousTouches = []

            if (rtcp.isInputReady()) {
                rtcp.inputSend({
                    id: 'TOUCHEND',
                    data: JSON.stringify(this.simultaneousTouches),
                })
            }
        },

        async onMouseMove(event: MouseEvent): Promise<void> {
            mEvent.dispatch(USER_INTERACTION)

            const { useStore } = await import('./main')
            const mainStore = useStore()

            if (!mainStore.capture) {
                return
            }

            const { useUserStore } = await import('./user')
            const userStore = useUserStore()

            if (
                userStore.user.player_preferences
                    .is_cursor_captured_mode_enabled
            ) {
                rtcp.inputSend({
                    id: 'MOUSEMOTION',
                    data: JSON.stringify({
                        isLeft: 0,
                        x: 0,
                        y: 0,
                        relx: event.movementX,
                        rely: event.movementY,
                        width: 0,
                        height: 0,
                    }),
                })

                return
            }

            const videoElement = _getVideoElement()

            this.convertRemoteCursorPositionToLocal({
                x: event.clientX,
                y: event.clientY,
            })

            await this.recalculateVideoMetrics()

            const remotePosition = _calculateRemotePositionFromEvent(
                event,
                this.video.centerOffset,
                this.video.serverToBrowserScale
            )
            this.cursor.remotePosition.x = remotePosition.x
            this.cursor.remotePosition.y = remotePosition.y

            // Don't send position if cursor is not in the video
            if (
                !this.isCursorInsideElement(
                    event.clientX,
                    event.clientY,
                    videoElement
                )
            ) {
                return
            }

            rtcp.inputSend({
                id: 'MOUSEPOSITION',
                data: JSON.stringify({
                    isLeft: 0,
                    x: Math.max(this.cursor.remotePosition.x, 0),
                    y: Math.max(this.cursor.remotePosition.y, 0),
                    relx: 0,
                    rely: 0,
                    width: 0,
                    height: 0,
                }),
            })
        },

        async onMouseUp(event: MouseEvent): Promise<void> {
            const { useStore } = await import('./main')
            const mainStore = useStore()

            if (!mainStore.capture) {
                return
            }

            this.pendingLocalClickDownEvent = null

            rtcp.inputSend({
                id: 'MOUSEUP',
                data: JSON.stringify({
                    x: this.cursor.remotePosition.x,
                    y: this.cursor.remotePosition.y,
                    button: event.button + 1,
                }),
            })
        },

        async onMouseDown(event: MouseEvent): Promise<void> {
            const { useStore } = await import('./main')
            const mainStore = useStore()

            if (!mainStore.capture) {
                return
            }

            const clickData = {
                id: 'MOUSEDOWN',
                data: JSON.stringify({
                    x: this.cursor.remotePosition.x,
                    y: this.cursor.remotePosition.y,
                    button: event.button + 1,
                }),
            }

            this.pendingLocalClickDownEvent = clickData

            rtcp.inputSend(clickData)
        },

        async onMouseWheel(event: WheelEvent): Promise<void> {
            const { useStore } = await import('./main')
            const { useUserStore } = await import('./user')
            const { playerMouseWheelScrollSensitivityEventsMultiplier } =
                await import('../enums/playerMouseWheelScrollSensitivity')

            const mainStore = useStore()
            const userStore = useUserStore()

            if (!mainStore.capture) {
                return
            }

            const multiplier =
                playerMouseWheelScrollSensitivityEventsMultiplier(
                    userStore.user.player_preferences
                        .mousewheel_scroll_sensitivity
                )

            const mouseWheelEventPayload = {
                id: 'MOUSEWHEEL' as const,
                data: JSON.stringify({
                    dx: event.deltaX,
                    dy: event.deltaY,
                    dz: event.deltaZ,
                    dm: event.deltaMode,
                }),
            }

            if (multiplier >= 1) {
                for (let ii = 0; ii < multiplier; ii++) {
                    rtcp.inputSend(mouseWheelEventPayload)
                }

                return
            }

            // Workaround: we can't send a fractional number of events,
            // so we accumulate scroll calls and only send once every N calls
            // Example: multiplier 0.25 = send 1 event every 4 calls
            this.mouseWheelThrottleCounter++

            const eventsSkippedBeforeSending = Math.round(1 / multiplier)

            if (this.mouseWheelThrottleCounter >= eventsSkippedBeforeSending) {
                this.mouseWheelThrottleCounter = 0
                rtcp.inputSend(mouseWheelEventPayload)
            }
        },

        async onTouchMove(event: PointerEvent): Promise<void> {
            const remotePosition = _calculateRemotePositionFromEvent(
                event,
                this.video.centerOffset,
                this.video.serverToBrowserScale
            )
            this.cursor.remotePosition.x = remotePosition.x
            this.cursor.remotePosition.y = remotePosition.y

            this.convertRemoteCursorPositionToLocal({
                x: event.clientX,
                y: event.clientY,
            })

            // Update touch positions for all active touches based on pointer ID
            for (const touch of this.simultaneousTouches) {
                if (touch && typeof touch === 'object' && 'id' in touch) {
                    const touchData = touch as TouchData
                    if (touchData.id === event.pointerId) {
                        touchData.x = Math.max(this.cursor.remotePosition.x, 0)
                        touchData.y = Math.max(this.cursor.remotePosition.y, 0)
                    }
                }
            }

            rtcp.inputSend({
                id: 'TOUCHPOSITION',
                data: JSON.stringify(this.simultaneousTouches),
            })
        },

        async onTouchStart(event: PointerEvent): Promise<void> {
            const { useStore } = await import('./main')
            const mainStore = useStore()

            if (!mainStore.capture) {
                return
            }

            mEvent.dispatch(USER_INTERACTION)

            const { useUserStore } = await import('./user')
            const userStore = useUserStore()

            if (
                userStore.user.player_preferences
                    .is_cursor_captured_mode_enabled
            ) {
                this.disableRemoteCursorCapturedMode()
            }

            await this.recalculateVideoMetrics()

            if (event.isPrimary) {
                const remotePosition = _calculateRemotePositionFromEvent(
                    event,
                    this.video.centerOffset,
                    this.video.serverToBrowserScale
                )
                this.cursor.remotePosition.x = remotePosition.x
                this.cursor.remotePosition.y = remotePosition.y
            }

            // Track simultaneous touches for multitouch support
            this.simultaneousTouches.push(this.createTouchDataFromEvent(event))

            rtcp.inputSend({
                id: 'TOUCHSTART',
                data: JSON.stringify(this.simultaneousTouches),
            })
            rtcp.inputSend({
                id: 'TOUCHPOSITION',
                data: JSON.stringify(this.simultaneousTouches),
            })
        },

        async onTouchEnd(event: PointerEvent): Promise<void> {
            const { useStore } = await import('./main')
            const mainStore = useStore()

            if (!mainStore.capture) {
                return
            }

            this.pendingLocalClickDownEvent = null

            const idx = this.findTouchIndexById(event.pointerId)
            this.simultaneousTouches.splice(idx, 1)
            rtcp.inputSend({
                id: 'TOUCHEND',
                data: JSON.stringify(this.simultaneousTouches),
            })
        },

        async onTouchCancel(): Promise<void> {
            const { useStore } = await import('./main')
            const mainStore = useStore()

            if (!mainStore.capture) {
                return
            }

            this.pendingLocalClickDownEvent = null

            this.clearAllTouches()
        },

        isCursorInsideElement(
            clientX: number,
            clientY: number,
            element: HTMLElement | null
        ): boolean {
            if (!element) {
                return false
            }

            const rect = element.getBoundingClientRect()

            return (
                clientX >= rect.left
                && clientX <= rect.right
                && clientY >= rect.top
                && clientY <= rect.bottom
            )
        },

        async onPointerLeavePlayerArea(e: PointerEvent): Promise<void> {
            const { useStore } = await import('./main')
            const { useMenuStore } = await import('./menu')
            const { useModalStore } = await import('./modal')
            const mainStore = useStore()
            const menuStore = useMenuStore()
            const modalStore = useModalStore()

            if (
                mainStore.readonly
                || menuStore.isSideMenuDisplayed
                || !!modalStore.current
            ) {
                return
            }

            if (e.pointerType === 'mouse') {
                mainStore.disableCapture()
            }

            const { useUserStore } = await import('./user')
            const userStore = useUserStore()

            if (
                userStore.user.player_preferences
                    .is_cursor_captured_mode_enabled
            ) {
                menuStore.toggleSideMenuDisplay()
            }
        },

        async onPointerEnterPlayerArea(e: PointerEvent): Promise<void> {
            const { useStore } = await import('./main')
            const { useMenuStore } = await import('./menu')
            const { useModalStore } = await import('./modal')
            const { useVdAgentStore } = await import('./vdAgent')
            const mainStore = useStore()
            const menuStore = useMenuStore()
            const modalStore = useModalStore()
            const vdAgentStore = useVdAgentStore()

            if (
                mainStore.readonly
                || menuStore.isSideMenuDisplayed
                || !!modalStore.current
            ) {
                return
            }

            if (vdAgentStore.isEnabled && e.pointerType === 'mouse') {
                mainStore.enableCapture()
            }
        },
    },
})
